import { type Grid, isBlankRow, parseDelimited } from './spreadsheet';

const BLOCKS = new Set(['P', 'DIV', 'LI']);

/**
 * Numbers quotes a cell with a line break in its plain text but leaves the
 * quotes inside it single, so the cell cannot be told from the next row and
 * the row splits in two. The table it copies alongside keeps it whole. Excel
 * and Sheets double those quotes, so their text is pasted as it is, and the
 * table is taken only when it counts other rows than the text.
 */
export function tableReplacingPaste(html: string, text: string): Grid | null {
    if (!html || !/<table/i.test(html)) return null;

    // An inert document: nothing in it loads or runs while it is read.
    const table = new DOMParser().parseFromString(html, 'text/html').querySelector('table');
    if (!table) return null;

    const grid = [...table.rows].map(row => [...row.cells].map(cellText)).filter(row => !isBlankRow(row));
    const textRows = parseDelimited(text).filter(row => !isBlankRow(row));

    return grid.length > 0 && grid.length !== textRows.length ? grid : null;
}

/** Tab-separated, quoting the cells that hold a tab, a line break or a quote, as Sheets does. */
export function toTabSeparated(grid: Grid): string {
    return grid
        .map(row => row.map(cell => (/[\t\n\r"]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join('\t'))
        .join('\n');
}

/** A cell's lines, whether a <br> or a paragraph breaks them. */
function cellText(cell: Element): string {
    let text = '';
    const walk = (node: Node) => {
        node.childNodes.forEach(child => {
            if (child.nodeType === Node.TEXT_NODE) {
                const value = (child.textContent ?? '').replace(/\s+/g, ' ');
                // The markup's own spacing between two paragraphs is not a line of the cell.
                if (value.trim() || (text && !text.endsWith('\n'))) text += value;
            } else if (child.nodeName === 'BR') {
                text += '\n';
            } else {
                const block = BLOCKS.has(child.nodeName);
                if (block && text && !text.endsWith('\n')) text += '\n';
                walk(child);
                if (block && !text.endsWith('\n')) text += '\n';
            }
        });
    };
    walk(cell);

    return text
        .split('\n')
        .map(line => line.trim())
        .join('\n')
        .trim();
}
